import { useEffect, useRef, useState } from 'react';
import { FilePlus2, Plus, Trash2, Check, Loader2, HelpCircle } from 'lucide-react';
import { languoidEnglishName } from '@kolectra/shared/languoids';
import {
  Connectedness,
  Preparedness,
  SocialDifferential,
  decodeRegister,
  encodeRegister,
} from '@kolectra/shared/registers';
import { Field } from '@kolectra/ui';

/** A draft to edit, seeded into the form via its json_template `record`. */
export interface EditSeed {
  id: string;
  segmentId: string;
  languoidCode: string;
  record: object;
}

/**
 * The single-segment create/edit form (web). Captures the full submission format
 * (json_template.json). The reasoning task (one question + its answer/level/
 * description) is authored once per segment (§0); the translation/script fields
 * are per utterance. In create mode the language is inherited and the id is
 * auto-drawn; in edit mode it's seeded from an existing draft. Per-language
 * domain suggestions, the 3-axis register picker, per-field autosave, and help
 * tooltips. On submit it hands the segment up; the parent adds or updates.
 */

type Level = 'hard' | 'extra hard';

const REGISTER_AXES = [
  { key: 'connectedness' as const, name: 'Connectedness', options: Object.values(Connectedness) },
  { key: 'preparedness' as const, name: 'Preparedness', options: Object.values(Preparedness) },
  { key: 'socialDifferential' as const, name: 'Social differential', options: Object.values(SocialDifferential) },
];

interface UtteranceForm {
  speaker: string;
  register: string;
  utteranceContext: string;
  text: string;
  translationLevel: Level;
  translationChallengeDescription: string;
  referenceTranslation: string;
}

interface FormValues {
  domain: string;
  segmentContext: string;
  // Challenge-level reasoning task (one per segment, §0).
  question: string;
  referenceAnswer: string;
  reasoningLevel: Level;
  reasoningChallengeDescription: string;
  utterances: UtteranceForm[];
}

export interface CreatedInfo {
  domain: string;
  recordingFlow: 'SOLO' | 'COORDINATED';
  utteranceCount: number;
  record: object;
}

function emptyUtterance(n: number): UtteranceForm {
  return {
    speaker: `U${String(n).padStart(2, '0')}`,
    register: 'mrk',
    utteranceContext: '',
    text: '',
    translationLevel: 'hard',
    translationChallengeDescription: '',
    referenceTranslation: '',
  };
}

// --- Reading a stored submission record back into the form ------------------

type RawUtt = Record<string, unknown>;
type RawRecord = { segment?: Record<string, unknown> & { utterances?: unknown } };

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const toLevel = (v: unknown): Level => (v === 'extra hard' ? 'extra hard' : 'hard');

function parseRecord(rec: unknown): FormValues | null {
  const seg = (rec as RawRecord)?.segment;
  if (!seg) return null;
  const list = Array.isArray(seg.utterances) ? (seg.utterances as RawUtt[]) : [];
  return {
    domain: str(seg.domain),
    segmentContext: str(seg.segment_context),
    question: str(seg.question),
    referenceAnswer: str(seg.reference_answer),
    reasoningLevel: toLevel(seg.reasoning_level),
    reasoningChallengeDescription: str(seg.reasoning_challenge_description),
    utterances: list.map((u, i) => ({
      speaker: str(u.utterance_speaker, `U${String(i + 1).padStart(2, '0')}`),
      register: str(u.register, 'mrk'),
      utteranceContext: str(u.utterance_context),
      text: str(u.text),
      translationLevel: toLevel(u.translation_level),
      translationChallengeDescription: str(u.translation_challenge_description),
      referenceTranslation: str(u.reference_translation),
    })),
  };
}

/** A blank form (fallback if a record can't be parsed). */
function emptyForm(): FormValues {
  return {
    domain: '',
    segmentContext: '',
    question: '',
    referenceAnswer: '',
    reasoningLevel: 'hard',
    reasoningChallengeDescription: '',
    utterances: [emptyUtterance(1)],
  };
}

function loadDraft(key: string): FormValues | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as FormValues) : null;
  } catch {
    return null;
  }
}

export function SingleCreate({
  segmentId,
  lang,
  domainSuggestions,
  edit,
  onSubmit,
  onCancel,
}: {
  segmentId: string;
  lang: string;
  domainSuggestions: string[];
  edit?: EditSeed | null;
  onSubmit: (info: CreatedInfo) => void;
  onCancel: () => void;
}) {
  const editing = edit ?? null;
  const effSegmentId = editing ? editing.segmentId : segmentId;
  const effLang = editing ? editing.languoidCode : lang;
  const fullId = `${effSegmentId}_${effLang}`;
  const autosaveKey = editing ? `kolectra.web.draft.edit.${editing.id}` : `kolectra.web.draft.${effSegmentId}_${effLang}`;

  const initial = useRef<FormValues | null>(
    editing ? parseRecord(editing.record) ?? emptyForm() : loadDraft(autosaveKey),
  ).current;

  const [domain, setDomain] = useState(initial?.domain ?? '');
  const [segmentContext, setSegmentContext] = useState(initial?.segmentContext ?? '');
  const [question, setQuestion] = useState(initial?.question ?? '');
  const [referenceAnswer, setReferenceAnswer] = useState(initial?.referenceAnswer ?? '');
  const [reasoningLevel, setReasoningLevel] = useState<Level>(initial?.reasoningLevel ?? 'hard');
  const [reasoningChallenge, setReasoningChallenge] = useState(initial?.reasoningChallengeDescription ?? '');
  const [utterances, setUtterances] = useState<UtteranceForm[]>(initial?.utterances ?? [emptyUtterance(1)]);

  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(initial ? Date.now() : null);
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setSaving(true);
    const t = window.setTimeout(() => {
      try {
        localStorage.setItem(
          autosaveKey,
          JSON.stringify({
            domain,
            segmentContext,
            question,
            referenceAnswer,
            reasoningLevel,
            reasoningChallengeDescription: reasoningChallenge,
            utterances,
          } satisfies FormValues),
        );
      } catch {
        /* ignore */
      }
      setSaving(false);
      setSavedAt(Date.now());
    }, 500);
    return () => window.clearTimeout(t);
  }, [domain, segmentContext, question, referenceAnswer, reasoningLevel, reasoningChallenge, utterances, autosaveKey]);

  function patch(i: number, p: Partial<UtteranceForm>) {
    setUtterances((u) => u.map((x, j) => (j === i ? { ...x, ...p } : x)));
  }
  function addUtterance() {
    setUtterances((u) => [...u, emptyUtterance(u.length + 1)]);
  }
  function removeUtterance(i: number) {
    setUtterances((u) => (u.length === 1 ? u : u.filter((_, j) => j !== i)));
  }

  const langLabel = languoidEnglishName(effLang) ?? effLang;

  const utteranceValid = (u: UtteranceForm) =>
    u.text.trim() && u.referenceTranslation.trim() && u.translationChallengeDescription.trim();
  const valid =
    domain.trim() !== '' &&
    segmentContext.trim() !== '' &&
    question.trim() !== '' &&
    referenceAnswer.trim() !== '' &&
    reasoningChallenge.trim() !== '' &&
    utterances.every(utteranceValid);

  function submit() {
    const record = {
      segment: {
        id: fullId,
        lang: effLang,
        segment_context: segmentContext.trim(),
        domain: domain.trim(),
        // The one reasoning task for the whole segment (§0).
        question: question.trim(),
        reference_answer: referenceAnswer.trim(),
        reasoning_level: reasoningLevel,
        reasoning_challenge_description: reasoningChallenge.trim(),
        utterances: utterances.map((u) => ({
          utterance_speaker: u.speaker,
          utterance_context: u.utteranceContext.trim(),
          register: u.register,
          text: u.text.trim(),
          audio_url: '(pending recording)',
          translation_level: u.translationLevel,
          translation_challenge_description: u.translationChallengeDescription.trim(),
          reference_translation: u.referenceTranslation.trim(),
        })),
      },
    };
    // Segment audio is one concatenated take per challenge (§0), so the flow is
    // always SOLO — a single recorder voices the whole segment.
    onSubmit({ domain: domain.trim(), recordingFlow: 'SOLO', utteranceCount: utterances.length, record });
    try {
      localStorage.removeItem(autosaveKey);
    } catch {
      /* ignore */
    }
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Autosave saving={saving} savedAt={savedAt} />
      </div>

      <datalist id="domain-suggestions">
        {domainSuggestions.map((d) => (
          <option key={d} value={d} />
        ))}
      </datalist>

      <div className="zone meta" style={{ marginBottom: 16 }}>
        <div className="zhead">
          <div className="zico">
            <FilePlus2 size={14} strokeWidth={2} />
          </div>
          <div className="ztitle">{editing ? 'Edit segment' : 'New segment'}</div>
          <div className="zdesc">
            <span className="id" style={{ marginRight: 8 }}>{fullId}</span>
            <span className="tag lang">{langLabel}</span>
          </div>
        </div>
        <Field label="Domain" help="The real-world setting the speech comes from — suggestions come from existing segments in this language." hint="type or pick">
          <input value={domain} onChange={(e) => setDomain(e.target.value)} list="domain-suggestions" placeholder="e.g. customer service" />
        </Field>
        <Field label="Segment context" help="Who's speaking and the mood — guidance for the recorder. Never shown to the model.">
          <textarea
            value={segmentContext}
            onChange={(e) => setSegmentContext(e.target.value)}
            placeholder="e.g. Conversation on the phone between a customer service associate and a customer"
          />
        </Field>
      </div>

      {utterances.map((u, i) => (
        <UtteranceCard
          key={i}
          index={i}
          u={u}
          canRemove={utterances.length > 1}
          onChange={(p) => patch(i, p)}
          onRemove={() => removeUtterance(i)}
        />
      ))}

      <button className="btn" style={{ marginTop: 4 }} onClick={addUtterance}>
        <Plus size={15} strokeWidth={2} /> Add utterance
      </button>

      <div className="zone meta" style={{ marginTop: 16 }}>
        <div className="zhead">
          <div className="zico">
            <HelpCircle size={14} strokeWidth={2} />
          </div>
          <div className="ztitle">Reasoning task</div>
          <div className="zdesc">one comprehension question for the whole segment</div>
        </div>
        <Field label="Question" help="A single English question that probes whether the model understood the segment — voiced once as the question audio.">
          <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="A question that probes comprehension…" />
        </Field>
        <Field label="Reference answer" help="The correct answer to the question — the reasoning task is judged against this.">
          <input value={referenceAnswer} onChange={(e) => setReferenceAnswer(e.target.value)} placeholder="The correct answer…" />
        </Field>
        <div className="two">
          <Field label="Reasoning challenge" help="What makes the reasoning hard.">
            <input value={reasoningChallenge} onChange={(e) => setReasoningChallenge(e.target.value)} placeholder="e.g. untranslatable content" />
          </Field>
          <Field label="Reasoning level" help="Expected difficulty of the reasoning task.">
            <LevelSelect value={reasoningLevel} onChange={setReasoningLevel} />
          </Field>
        </div>
      </div>

      <div className="toolbar" style={{ justifyContent: 'flex-end', marginTop: 18 }}>
        <button className="btn ghost" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn pri" onClick={submit} disabled={!valid}>
          <FilePlus2 size={15} strokeWidth={1.8} /> {editing ? 'Save changes' : 'Create as draft'}
        </button>
      </div>
    </>
  );
}

function Autosave({ saving, savedAt }: { saving: boolean; savedAt: number | null }) {
  if (saving) {
    return (
      <span className="mini" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--ink-soft)' }}>
        <Loader2 size={13} strokeWidth={2} className="spin" /> Saving…
      </span>
    );
  }
  if (savedAt) {
    return (
      <span className="mini" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--good)' }}>
        <Check size={13} strokeWidth={2.5} /> Draft saved
      </span>
    );
  }
  return <span className="mini">Autosaves as you type</span>;
}

function UtteranceCard({
  index,
  u,
  canRemove,
  onChange,
  onRemove,
}: {
  index: number;
  u: UtteranceForm;
  canRemove: boolean;
  onChange: (p: Partial<UtteranceForm>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="card" style={{ padding: 16, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span className="id">Utterance {index + 1}</span>
        <input
          value={u.speaker}
          onChange={(e) => onChange({ speaker: e.target.value })}
          style={{ width: 80, fontFamily: 'var(--mono)', fontSize: 12, padding: '4px 8px', border: '1px solid var(--line)', borderRadius: 5 }}
          title="Speaker label"
        />
        <div style={{ flex: 1 }} />
        {canRemove && (
          <button className="iconbtn danger" title="Remove utterance" onClick={onRemove}>
            <Trash2 strokeWidth={1.8} />
          </button>
        )}
      </div>

      <Field label="Text" help="The verbatim line the recorder reads aloud. The model only ever hears the audio, never this text.">
        <textarea value={u.text} onChange={(e) => onChange({ text: e.target.value })} placeholder="The verbatim line to be spoken…" />
      </Field>

      <Field label="Utterance context" help="Tone, grammatical gender, relationship — context for the recorder.">
        <input value={u.utteranceContext} onChange={(e) => onChange({ utteranceContext: e.target.value })} placeholder="e.g. Both interlocutors are feminine; the tone is neutral." />
      </Field>

      <div className="field">
        <label>
          Register
          <span className="idot" tabIndex={0} title="The style of language for the situation, built on 3 axes → a 3-character code.">
            ?
          </span>
          <span className="hint"> 3 axes → 3-char code</span>
        </label>
        <RegisterPicker value={u.register} onChange={(register) => onChange({ register })} />
      </div>

      <Field label="Reference translation" help="The faithful reference translation — the translation task is judged against this.">
        <textarea value={u.referenceTranslation} onChange={(e) => onChange({ referenceTranslation: e.target.value })} />
      </Field>

      <div className="two">
        <Field label="Translation challenge" help="What makes the translation hard.">
          <input value={u.translationChallengeDescription} onChange={(e) => onChange({ translationChallengeDescription: e.target.value })} placeholder="e.g. named entity" />
        </Field>
        <Field label="Translation level" help="How hard you expect translating this to be.">
          <LevelSelect value={u.translationLevel} onChange={(translationLevel) => onChange({ translationLevel })} />
        </Field>
      </div>
    </div>
  );
}

function LevelSelect({ value, onChange }: { value: Level; onChange: (v: Level) => void }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value as Level)}>
      <option value="hard">hard</option>
      <option value="extra hard">extra hard</option>
    </select>
  );
}

function RegisterPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const tri = decodeRegister(value) ?? decodeRegister('mrk')!;
  const words = REGISTER_AXES.map((ax) => ax.options.find((o) => o.letter === tri[ax.key])?.name ?? '?').join(' · ');
  return (
    <>
      <div className="regpick">
        {REGISTER_AXES.map((ax) => (
          <div key={ax.key} className="regaxis">
            <div className="ah">{ax.name}</div>
            {ax.options.map((o) => (
              <div
                key={o.letter}
                className={'opt' + (tri[ax.key] === o.letter ? ' on' : '')}
                title={o.description}
                onClick={() => onChange(encodeRegister({ ...tri, [ax.key]: o.letter }))}
              >
                {o.name}
                <span className="c">{o.letter}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="regout">
        Resolved code <b>{value}</b>
        <span style={{ color: 'var(--ink-faint)', fontSize: 11, marginLeft: 'auto' }}>{words}</span>
      </div>
    </>
  );
}
